import { GetApp } from "@mui/icons-material"
import { Alert, Box, IconButton, InputAdornment, TextField, Tooltip } from "@mui/material"
import log from "loglevel"
import { useEffect, useState } from "react"
import { Body, Label } from "../../components/Typography/Typography"
import { useNexusMissionIdOptional } from "../../hooks/useNexusParams"
import { FlightlineEvent } from "../../models/FlightlineEvent"
import { FlightlineStatusOption } from "../../store/types/MissionsTypes"
import { rangeToList } from "../../tools"
import { sortFlightlineEvents } from "../../utils/FlightlineEventUtils"
import { useFlightlineEventStore } from "../../zstore/FlightlineEventStore"
import { useStyles } from "./styles"

export const FlightlineImport = () => {
    const { classes, cx } = useStyles()
    const [localInput, setLocalInput] = useState("")
    const [error, setError] = useState("")
    const missionId = useNexusMissionIdOptional()

    const selectedFlightplans = useFlightlineEventStore((state) => state.selectedFlightplans)
    const eventPlannedLines = useFlightlineEventStore((state) => state.eventPlannedLines)
    const importRange = useFlightlineEventStore((state) => state.importRange)
    const sortOrder = useFlightlineEventStore((state) => state.sortOrder)
    const updateFlightlineEvents = useFlightlineEventStore((state) => state.updateFlightlineEvents)
    const updateImportRange = useFlightlineEventStore((state) => state.updateImportRange)

    useEffect(() => {
        setError("")
    }, [])

    const handleImport = () => {
        let parsedRange: number[] = []
        // convert flightplans to array of ids
        const selectedFlightPlanIds = selectedFlightplans.map((plan) => plan.flight_plan_id)

        try {
            parsedRange = rangeToList(localInput)
        } catch (error) {
            setError(`Error parsing range ${localInput}. Error is: ${error.message}`)
            log.error(error)
            return
        }

        const newFlightlineEvents: FlightlineEvent[] = []
        parsedRange.forEach((num, index) => {
            // get flightPlannedLines that are in "range" using flight_line_names
            const plannedLine = eventPlannedLines.find((pl) => +pl.plannedLineName === num)
            if (plannedLine && selectedFlightPlanIds.includes(Number(plannedLine.flightPlanId))) {
                newFlightlineEvents.push({
                    fe_event_id: index + 1,
                    mission_id: missionId ? +missionId : null,
                    coll_event_name: "",
                    flight_line_distance: 0,
                    flight_line_duration: 0,
                    flight_line_start_time: "",
                    flight_line_stop_time: "",
                    flight_status: FlightlineStatusOption.NORMAL,
                    flown_agl: 0,
                    line_original_name: "",
                    notes: "",
                    sensor_details: null,
                    fe_planned_line_ids: [plannedLine.plannedLineId],
                })
            }
        })

        const sortedEvents = sortFlightlineEvents(newFlightlineEvents, sortOrder)
        updateFlightlineEvents(sortedEvents)
        updateImportRange(localInput)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter") {
            handleImport()
        }
    }

    return (
        <Box className={classes.gutterBottom__lg}>
            <Label>Import Flightplanned Lines</Label>

            <Body gutterBottom="sm" color="textSecondary" className={classes.import__text}>
                Enter a range below to match flightplanned lines by their name and import them as flightline events.
                Range must include positive integers and leading zeros are ignored.
            </Body>
            <Body gutterBottom="sm" color="textSecondary" className={classes.import__text}>
                Warning: Importing a new range may delete previous ranges if they have been removed from the input
                field. Please include all target lines in your range.
            </Body>
            <Body gutterBottom="lg" color="textSecondary" className={classes.import__text}>
                Example: 5, 1-3, 8, 10-13
            </Body>

            <TextField
                label="Enter Range"
                variant="outlined"
                value={localInput}
                error={Boolean(error)}
                helperText={error}
                onChange={(e) => setLocalInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className={cx(classes.import__range, classes.input__md, classes.gutterBottom__sm)}
                slotProps={{
                    input: {
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title="Import & Overwrite">
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={!!error || importRange === localInput}
                                            onClick={handleImport}
                                        >
                                            <GetApp />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    },
                }}
            />
            {error && <Alert severity="error">{error}</Alert>}
        </Box>
    )
}
